import { Controller } from "@hotwired/stimulus"

/**
 * Row Select Controller
 * 테이블 행 클릭 선택 + back 네비게이션 시 하이라이트 유지
 *
 * 사용법:
 *   <table data-controller="row-select"
 *          data-row-select-storage-key-value="products_selected_row"
 *          data-row-select-row-id-prefix-value="product-row-">
 *     <tbody>
 *       <tr id="product-row-123" data-action="click->row-select#selectRow"> ...
 */
export default class extends Controller {
  static values = {
    storageKey: String,                          // sessionStorage key (페이지별 고유)
    rowIdPrefix: { type: String, default: "product-row-" }  // 행 id 접두사
  }

  connect() {
    this.applyHighlight()
    this._onTurboLoad = () => this.applyHighlight()
    this._onTurboRender = () => this.applyHighlight()
    this._onTurboBeforeCache = () => this.clearSelection()

    document.addEventListener("turbo:load", this._onTurboLoad)
    document.addEventListener("turbo:render", this._onTurboRender)
    document.addEventListener("turbo:before-cache", this._onTurboBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this._onTurboLoad)
    document.removeEventListener("turbo:render", this._onTurboRender)
    document.removeEventListener("turbo:before-cache", this._onTurboBeforeCache)
  }

  // 행 클릭 이벤트 (data-action="click->row-select#selectRow")
  selectRow(event) {
    // 링크 클릭 시 → sessionStorage에 저장 후 기본 동작 유지
    if (event.target.closest("a")) {
      const row = event.target.closest("tr")
      if (row) this.storeRowId(row)
      return
    }

    // 체크박스, 버튼 클릭은 무시
    if (event.target.closest("input, button, label")) return

    const row = event.target.closest("tr")
    if (!row) return

    // 이미 선택된 행을 다시 클릭하면 해제
    if (row.classList.contains("row-selected")) {
      row.classList.remove("row-selected")
      return
    }

    // 기존 선택 해제 후 새 행 선택
    this.clearSelection()
    row.classList.add("row-selected")
  }

  applyHighlight() {
    const highlightId = new URLSearchParams(window.location.search).get("highlight")
                     || sessionStorage.getItem(this.storageKeyValue)
    if (!highlightId) return

    this.clearSelection()

    // 1차: data-row-select-value 속성 기반 (한 값에 여러 row 매칭 — KOSHA 같은 rowspan 테이블).
    const valueRows = this.element.querySelectorAll(`[data-row-select-value="${CSS.escape(highlightId)}"]`)
    if (valueRows.length > 0) {
      valueRows.forEach(r => r.classList.add("row-selected"))
      valueRows[0].scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    // 2차: id 기반 (단일 row 페이지 — 기본 동작).
    const row = document.getElementById(this.rowIdPrefixValue + highlightId)
    if (row) {
      row.classList.add("row-selected")
      row.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  clearSelection() {
    this.element.querySelectorAll("tr.row-selected").forEach(tr => {
      tr.classList.remove("row-selected")
    })
  }

  storeRowId(row) {
    const id = row.id.replace(this.rowIdPrefixValue, "")
    if (id) sessionStorage.setItem(this.storageKeyValue, id)
  }
}
